import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Mail, MessageSquare, Globe } from "lucide-react";
import { PageHeader, Card, Field, TextArea } from "@/components/admin/Primitives";
import { useSettings } from "@/context/SettingsContext";

const EMPTY = {
  site_name: "", tagline: "", whatsapp_number: "", contact_email: "", contact_phone: "", address: "",
  facebook_url: "", instagram_url: "", youtube_url: "", map_embed: "",
  brevo_api_key: "", brevo_sender_email: "", brevo_sender_name: "",
  smtp_host: "", smtp_port: "", smtp_user: "", smtp_password: "", smtp_from: "",
  notifylk_user_id: "", notifylk_api_key: "", notifylk_sender_id: "NotifyDEMO",
};

export default function Settings() {
  const { setSettings } = useSettings();
  const [tab, setTab] = useState("general");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setForm({ ...EMPTY, ...data })); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const TABS = [
    { key: "general", label: "General", icon: Globe },
    { key: "email", label: "Email (Brevo / SMTP)", icon: Mail },
    { key: "sms", label: "SMS (notify.lk)", icon: MessageSquare },
  ];

  return (
    <div data-testid="admin-settings-page" className="text-white">
      <PageHeader title="Site Settings" subtitle="Brand, contact info, WhatsApp number, socials + email & SMS provider keys." />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`} className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.12em] font-bold inline-flex items-center gap-2 ${tab === t.key ? "bg-[var(--pz-red)] text-white" : "border border-[var(--pz-line)] text-zinc-400 hover:border-[var(--pz-red)]"}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <Card>
        <form onSubmit={save} className="space-y-4">
          {tab === "general" && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Site name" value={form.site_name} onChange={(v) => setForm({ ...form, site_name: v })} testid="settings-site-name" required />
                <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="settings-tagline" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="WhatsApp number (digits only)" value={form.whatsapp_number} onChange={(v) => setForm({ ...form, whatsapp_number: v })} testid="settings-wa" placeholder="94770000000" required />
                <Field label="Contact phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="settings-phone" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Contact email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="settings-email" />
                <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="settings-address" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Facebook URL" value={form.facebook_url} onChange={(v) => setForm({ ...form, facebook_url: v })} testid="settings-fb" />
                <Field label="Instagram URL" value={form.instagram_url} onChange={(v) => setForm({ ...form, instagram_url: v })} testid="settings-ig" />
                <Field label="YouTube URL" value={form.youtube_url} onChange={(v) => setForm({ ...form, youtube_url: v })} testid="settings-yt" />
              </div>
              <TextArea label="Map embed (iframe HTML, optional)" rows={4} value={form.map_embed} onChange={(v) => setForm({ ...form, map_embed: v })} testid="settings-map" />
            </>
          )}

          {tab === "email" && (
            <>
              <p className="text-xs text-zinc-400 -mt-2">Configure Brevo (preferred) or SMTP for bulk email. Leave both blank to fall back to environment Resend.</p>
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--pz-red)] mb-3">Brevo (recommended)</div>
                <Field label="Brevo API Key" value={form.brevo_api_key} onChange={(v) => setForm({ ...form, brevo_api_key: v })} testid="settings-brevo-key" placeholder="xkeysib-..." />
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <Field label="Sender email (verified in Brevo)" value={form.brevo_sender_email} onChange={(v) => setForm({ ...form, brevo_sender_email: v })} testid="settings-brevo-sender" placeholder="noreply@yourdomain.com" />
                  <Field label="Sender name" value={form.brevo_sender_name} onChange={(v) => setForm({ ...form, brevo_sender_name: v })} testid="settings-brevo-name" />
                </div>
              </div>
              <div className="pt-4 border-t border-[var(--pz-line)]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--pz-red)] mb-3">Or SMTP (static email server)</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="SMTP host" value={form.smtp_host} onChange={(v) => setForm({ ...form, smtp_host: v })} testid="settings-smtp-host" placeholder="smtp.gmail.com" />
                  <Field label="SMTP port" value={form.smtp_port} onChange={(v) => setForm({ ...form, smtp_port: v })} testid="settings-smtp-port" placeholder="587" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <Field label="SMTP username" value={form.smtp_user} onChange={(v) => setForm({ ...form, smtp_user: v })} testid="settings-smtp-user" />
                  <Field label="SMTP password" type="password" value={form.smtp_password} onChange={(v) => setForm({ ...form, smtp_password: v })} testid="settings-smtp-pass" />
                </div>
                <div className="mt-3">
                  <Field label="From address" value={form.smtp_from} onChange={(v) => setForm({ ...form, smtp_from: v })} testid="settings-smtp-from" placeholder="hello@arena.com" />
                </div>
              </div>
            </>
          )}

          {tab === "sms" && (
            <>
              <p className="text-xs text-zinc-400 -mt-2">notify.lk credentials for bulk SMS. Get these from your notify.lk dashboard.</p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                <Field label="User ID" value={form.notifylk_user_id} onChange={(v) => setForm({ ...form, notifylk_user_id: v })} testid="settings-notify-user" />
                <Field label="API Key" value={form.notifylk_api_key} onChange={(v) => setForm({ ...form, notifylk_api_key: v })} testid="settings-notify-key" />
              </div>
              <Field label="Sender ID (max 11 chars)" value={form.notifylk_sender_id} onChange={(v) => setForm({ ...form, notifylk_sender_id: v })} testid="settings-notify-sender" placeholder="NotifyDEMO" />
            </>
          )}

          <div className="pt-4 border-t border-[var(--pz-line)] flex justify-end">
            <button disabled={busy} data-testid="settings-save-btn" className="btn-red disabled:opacity-60"><Save className="h-4 w-4" /> {busy ? "Saving..." : "Save settings"}</button>
          </div>
        </form>
      </Card>
    </div>
  );
}
